import type { LocationQuery } from 'vue-router';
import type { RouteTransfer } from '@/types/RouteTransferType';
import type { GetMultiTransactionEntryBackRouteNameParams } from '@/modules/multiTransactionV2/types/rfqMultiTransaction.type';

import { TRANSACTION_ROUTE_NAME, HOME_ROUTE_NAME } from '@/modules/multiTransactionV2/constants/wallet.constant';
import {
  MULTI_TRANSACTION_RFQ_QUERY_PARAM,
  MULTI_TRANSACTION_RFQ_QUERY_VALUE,
  RFQ_ROUTE_NAMES,
} from '@/shared/common/constants/rfqRoutes.constant';
import { RFQ_MULTI_TRANSACTION_COUNTRY_CODES_FALLBACK } from '@/modules/multiTransactionV2/constants/rfqFlow.constant';

export function isRfqMultiTransactionFlow(query: LocationQuery): boolean {
  return (
    query[MULTI_TRANSACTION_RFQ_QUERY_PARAM] ===
    MULTI_TRANSACTION_RFQ_QUERY_VALUE
  );
}

/**
 * Resuelve la ruta de retorno al salir del primer paso del multienvío (barra superior / volver).
 * Alinea la salida con Mesa de dinero: hub solo si ambos flags de config RFQ están activos;
 * si el usuario entró directo por remittance (`rfq-send`), vuelve al home.
 * @param params - Flags del flujo RFQ y de la config general (`isEnabledRfq`, `isEnabledRfqRemittance`).
 * @returns Nombre de ruta: `rfq` (hub), `movements` (home) o `transaction` (flujo normal).
 */
export function getMultiTransactionEntryBackRouteName({
  isRfqFlow,
  isEnabledRfq,
  isEnabledRfqRemittance,
}: GetMultiTransactionEntryBackRouteNameParams): string {
  if (!isRfqFlow) {
    return TRANSACTION_ROUTE_NAME;
  }

  const shouldReturnToRfqHub = isEnabledRfq && isEnabledRfqRemittance;

  return shouldReturnToRfqHub ? RFQ_ROUTE_NAMES.HUB : HOME_ROUTE_NAME;
}

/**
 * Países permitidos para multienvío RFQ (`allowedCountriesRfqRemittance` del store).
 * @param allowedCountries - Valor de `enabledRfqMultiSendCountries` en `rfqGeneralConfig`.
 */
export function getRfqMultiTransactionCountryCodes(
  allowedCountries: string[]
): string[] {
  return allowedCountries.length > 0
    ? allowedCountries
    : [...RFQ_MULTI_TRANSACTION_COUNTRY_CODES_FALLBACK];
}

export function hasMultipleRfqMultiSendCountries(
  allowedCountries: string[]
): boolean {
  return allowedCountries.length > 1;
}

/**
 * Indica si el paso de selección de país/moneda destino debe mostrarse en el stepper.
 * @param isRfqFlow - Si la ruta incluye `?flow=rfq`.
 * @param allowedCountries - Países habilitados en configuración general RFQ.
 */
export function shouldShowRfqMultiSendCountryStep(
  isRfqFlow: boolean,
  allowedCountries: string[]
): boolean {
  return isRfqFlow && hasMultipleRfqMultiSendCountries(allowedCountries);
}

/**
 * Países enviados al listado de contactos en flujo RFQ.
 * Prioriza el país elegido en el paso opcional; si no hay selección, usa la config o fallback.
 * @param selectedCountry - País guardado en `rfqGeneralConfig` tras elegir o auto-selección.
 * @param allowedCountries - Países habilitados en configuración general RFQ.
 */
export function getRfqMultiSendCountryCodesForContacts(
  selectedCountry: string | null,
  allowedCountries: string[]
): string[] {
  if (selectedCountry) {
    return [selectedCountry];
  }

  return getRfqMultiTransactionCountryCodes(allowedCountries);
}

/**
 * Filtra rutas de remesa por códigos de país destino permitidos (alpha2).
 */
export function filterRemittanceRoutesByCountryCodes(
  routes: RouteTransfer[],
  countryCodes: string[]
): RouteTransfer[] {
  if (!countryCodes.length) {
    return routes;
  }

  const allowedCodes = new Set(
    countryCodes.map((countryCode) => countryCode.toUpperCase())
  );

  return routes.filter((route) =>
    allowedCodes.has(route.country?.alpha2Code?.toUpperCase() ?? '')
  );
}
